import type { BasketRepoError } from "@chatfood/dbe-client/errors";
import { useToast } from "@chatfood/design-system";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useModalHubStore } from "~/components/modal-hub";
import { BusinessRuleErrorEnum } from "~/composables/use-business-rules-validation/business-rule-error.enum";

const extractLineId = (
  context: Record<string, unknown> | null | undefined,
): string | undefined => {
  if (!context) return undefined;

  const single = context.itemId ?? context.item_id;
  if (typeof single === "string" && single) return single;

  const many = context.itemIds ?? context.item_ids;
  if (Array.isArray(many) && typeof many[0] === "string") return many[0];

  return undefined;
};

export const useDiscountEligibleItemsModal = () => {
  const basketStore = useBasketStore();
  const modalHub = useModalHubStore();
  const { t } = useLocale();
  const { toastUnexpectedError } = useToast();

  const removeDiscountAndItem = async (lineId: string) => {
    modalHub.setWarningConfirmLoading();

    try {
      await basketStore.removeDiscountAndItem(lineId);
    } catch {
      toastUnexpectedError();
    } finally {
      modalHub.hideWarning();
    }
  };

  const showDiscountEligibleItemsModal = (lineId: string) => {
    modalHub.hideLoading();
    modalHub.showWarning({
      title: t(
        "composables.use_business_rules_validation.discount_eligible_items_missing_modal.title",
      ),
      message: t(
        "composables.use_business_rules_validation.discount_eligible_items_missing_modal.message",
      ),
      dismissLabel: t(
        "composables.use_business_rules_validation.discount_eligible_items_missing_modal.dismiss_label",
      ),
      confirmLabel: t(
        "composables.use_business_rules_validation.discount_eligible_items_missing_modal.confirm_label",
      ),
      onWarningConfirm: () => removeDiscountAndItem(lineId),
    });
  };

  const tryHandleDiscountConflict = (
    error: BasketRepoError<"businessRule">,
  ): boolean => {
    if (
      error.meta?.violation !==
      BusinessRuleErrorEnum.DISCOUNT_ELIGIBLE_ITEMS_MISSING
    ) {
      return false;
    }

    const lineId = extractLineId(
      error.meta?.context as unknown as Record<string, unknown>,
    );
    if (!lineId) return false;

    showDiscountEligibleItemsModal(lineId);
    return true;
  };

  return { tryHandleDiscountConflict };
};
